import { useEffect, useLayoutEffect, useMemo, useRef } from 'react'
import * as THREE from 'three'
import { STELLING, prng, useIndeling } from './indeling'
import { useLicht, useMaterialen, useObjecten, useOppervlak } from '../scene/store'
import { useOppervlakken } from './useOppervlakken'
import { variatieKleuren, vulInstanties } from './instanties'
import { ATLAS_KOLOMMEN, draagvermogen, locatieAtlas } from './etiketten'

/** Hoogte van één staandertegel: twee sleuven van 50 mm (zie `texturen.ts`). */
const STAANDER_TEGEL = 0.1

/**
 * Hoe fel een lichtlijn is, als veelvoud van de accentkleur: boven de
 * bloomdrempel, zodat hij gloeit, maar niet zo fel dat AgX hem naar wit trekt.
 */
const LICHTLIJN_FEL = 12

/**
 * Een materiaal dat per instantie een vak uit de labelatlas kiest. Eén draw
 * call voor honderden verschillende labels: de instantie draagt alleen een
 * verschuiving mee, de rest is gedeeld.
 */
function atlasMateriaal(map: THREE.Texture) {
  const m = new THREE.MeshStandardMaterial({ map, roughness: 0.45, metalness: 0 })
  m.onBeforeCompile = (sh) => {
    sh.vertexShader = sh.vertexShader
      .replace('#include <uv_pars_vertex>', '#include <uv_pars_vertex>\nattribute vec2 atlasVak;')
      .replace(
        '#include <uv_vertex>',
        `#include <uv_vertex>\n#ifdef USE_MAP\nvMapUv = vMapUv / ${ATLAS_KOLOMMEN.toFixed(1)} + atlasVak;\n#endif`,
      )
  }
  m.customProgramCacheKey = () => 'atlas'
  return m
}

/**
 * Het stellingframe. Niet vier maar acht constructiedelen: staanders, voetplaten,
 * liggers, liggerconnectoren, diagonale en horizontale schoren, doorschuif-
 * beveiliging en aanrijdbeveiliging. De schoren zijn wat een stelling herkenbaar
 * maakt als stelling in plaats van als raster.
 *
 * Sinds de oppervlakteslag krijgt elk deel een staaltextuur én een eigen
 * kleurafwijking. Die tweede is hier belangrijker dan de eerste: er staan ruim
 * 400 staanders in beeld en die waren tot nu toe alle 400 exact `#2b3039`. Een
 * rij identieke objecten leest als één patroon in plaats van als 400 dingen —
 * dat is de reden dat een echte stelling, waarvan elke staander een andere
 * partij poedercoat en een ander stootje heeft, er *voller* uitziet bij precies
 * dezelfde geometrie.
 */
export function Stellingen() {
  const ind = useIndeling()
  const mat = useMaterialen()
  const obj = useObjecten()
  const opp = useOppervlak()
  const licht = useLicht()
  const set = useOppervlakken()

  const staanders = useRef<THREE.InstancedMesh>(null)
  const voetplaten = useRef<THREE.InstancedMesh>(null)
  const liggers = useRef<THREE.InstancedMesh>(null)
  const connectoren = useRef<THREE.InstancedMesh>(null)
  const diagonalen = useRef<THREE.InstancedMesh>(null)
  const horizontalen = useRef<THREE.InstancedMesh>(null)
  const doorschuif = useRef<THREE.InstancedMesh>(null)
  const beveiliging = useRef<THREE.InstancedMesh>(null)
  const labels = useRef<THREE.InstancedMesh>(null)
  const platen = useRef<THREE.InstancedMesh>(null)
  const lichtlijnen = useRef<THREE.InstancedMesh>(null)

  const opbouw = useMemo(() => {
    const { cfg, bayX, stellingHoogte, wanden } = ind
    const rnd = prng(cfg.seed + 77)
    const d = new THREE.Object3D()

    const staanderM: THREE.Matrix4[] = []
    const voetM: THREE.Matrix4[] = []
    const liggerM: THREE.Matrix4[] = []
    const connM: THREE.Matrix4[] = []
    const diagM: THREE.Matrix4[] = []
    const horM: THREE.Matrix4[] = []
    const dsM: THREE.Matrix4[] = []
    const bevM: THREE.Matrix4[] = []
    const labelM: THREE.Matrix4[] = []
    const labelVak: number[] = []
    const plaatM: THREE.Matrix4[] = []
    const lijnM: THREE.Matrix4[] = []

    const halveBay = STELLING.bayBreedte / 2
    const dzVoor = -STELLING.diepte / 2 + 0.08
    const dzAchter = STELLING.diepte / 2 - 0.08
    const diepteSpan = dzAchter - dzVoor

    const zet = (arr: THREE.Matrix4[]) => { d.updateMatrix(); arr.push(d.matrix.clone()) }

    for (const wand of wanden) {
      for (let i = 0; i <= cfg.bays; i++) {
        const x = bayX(i) - halveBay

        for (const dz of [dzVoor, dzAchter]) {
          // Staander
          d.position.set(x, stellingHoogte / 2, wand.z + dz)
          d.rotation.set(0, 0, 0); d.scale.set(1, 1, 1)
          zet(staanderM)

          // Voetplaat
          d.position.set(x, 0.012, wand.z + dz)
          zet(voetM)
        }

        // Schoren tussen de twee staanders van dit frame: afwisselend diagonaal
        // en horizontaal, zoals een echt vakwerk.
        if (obj.schoren) {
          const hoek = Math.atan2(diepteSpan, STELLING.niveauHoogte)
          const lengte = Math.hypot(diepteSpan, STELLING.niveauHoogte)
          for (let n = 0; n < cfg.niveaus; n++) {
            const y0 = n * STELLING.niveauHoogte
            // diagonaal, richting wisselt per niveau
            d.position.set(x, y0 + STELLING.niveauHoogte / 2, wand.z)
            d.rotation.set(n % 2 === 0 ? hoek : -hoek, 0, 0)
            d.scale.set(1, lengte / 0.9, 1)
            zet(diagM)
            // horizontaal op elk niveau
            d.position.set(x, y0 + STELLING.niveauHoogte, wand.z)
            d.rotation.set(0, 0, 0); d.scale.set(1, 1, 1)
            zet(horM)
          }
        }
      }

      for (let b = 0; b < cfg.bays; b++) {
        // De dwarsgang: alleen de staanders aan weerszijden blijven staan.
        if (b === ind.dwarsgang) continue
        const x = bayX(b)
        for (let n = 0; n < cfg.niveaus; n++) {
          const y = (n + 1) * STELLING.niveauHoogte

          for (const dz of [dzVoor + 0.02, dzAchter - 0.02]) {
            // Ligger
            d.position.set(x, y, wand.z + dz)
            d.rotation.set(0, 0, 0); d.scale.set(1, 1, 1)
            zet(liggerM)

            // Connectorplaten aan beide uiteinden van de ligger
            for (const zijde of [-1, 1]) {
              d.position.set(x + zijde * (halveBay - 0.04), y, wand.z + dz)
              zet(connM)
            }
          }

          // Locatielabel op de ligger aan de gangpadkant, midden in het vak.
          const gangZijde = wand.kant === -1 ? 1 : -1
          d.position.set(x, y, wand.z + gangZijde * (dzAchter - 0.02 + 0.031))
          d.rotation.set(0, gangZijde === 1 ? 0 : Math.PI, 0); d.scale.set(1, 1, 1)
          zet(labelM)
          labelVak.push(Math.floor(rnd() * ATLAS_KOLOMMEN * ATLAS_KOLOMMEN))
          d.rotation.set(0, 0, 0)

          // Lichtlijn onder de ligger aan de gangpadkant (22 oktober): de rode
          // lijnen langs de stellingen uit "From warehouse to powerhouse".
          // Altijd berekend, ook als hij uit staat: de schakelaar hoeft zo
          // niet de hele stelling opnieuw op te bouwen.
          d.position.set(x, y - STELLING.liggerHoogte / 2 - 0.012, wand.z + gangZijde * (dzAchter - 0.02 + 0.012))
          zet(lijnM)

          // Doorschuifbeveiliging op een deel van de niveaus
          if (rnd() > 0.55) {
            d.position.set(x, y + 0.26, wand.z + dzAchter - 0.04)
            d.rotation.set(0, 0, 0); d.scale.set(1, 1, 1)
            zet(dsM)
          }
        }
      }

      // Draagvermogenplaat op beide koppen van de stelling, op ooghoogte aan de
      // buitenste staander, naar buiten gericht.
      for (const kop of [-1, 1]) {
        const xk = kop === -1 ? bayX(0) - halveBay : bayX(cfg.bays - 1) + halveBay
        d.position.set(xk + kop * (STELLING.staanderDikte / 2 + 0.004), 1.55, wand.z)
        d.rotation.set(0, kop * Math.PI / 2, 0); d.scale.set(1, 1, 1)
        zet(plaatM)
      }
      d.rotation.set(0, 0, 0)

      // Aanrijdbeveiliging: geel, aan de gangpadzijde, op de buitenste staanders
      // van elke tweede bay — dat is waar een truck hem daadwerkelijk raakt.
      if (obj.aanrijdbeveiliging) {
        const dzGang = wand.kant === 1 ? dzVoor : dzAchter
        for (let i = 0; i <= cfg.bays; i++) {
          // Ook op beide staanders langs de dwarsgang: daar rijdt de C-MATIC.
          const langsDwarsgang = ind.dwarsgang !== null && (i === ind.dwarsgang || i === ind.dwarsgang + 1)
          if (i % 2 !== 0 && !langsDwarsgang) continue
          d.position.set(bayX(i) - halveBay, 0.22, wand.z + dzGang * 1.25)
          d.rotation.set(0, 0, 0); d.scale.set(1, 1, 1)
          zet(bevM)
        }
      }
    }

    return { staanderM, voetM, liggerM, connM, diagM, horM, dsM, bevM, labelM, labelVak, plaatM, lijnM }
  }, [ind, obj.schoren, obj.aanrijdbeveiliging])

  /**
   * De kleurafwijking per instantie. Staanders krijgen de ruimste marge: die
   * staan in het gangpad en worden in het echt het vaakst geraakt en bijgewerkt.
   * De gele aanrijdbeveiliging krijgt juist weinig — dat is een veiligheidskleur
   * en die moet als één kleur blijven lezen.
   */
  const kleuren = useMemo(() => {
    const v = opp.variatie
    const s = ind.cfg.seed
    return {
      staander: variatieKleuren(opbouw.staanderM.length, v * 1.15, s + 1),
      voet: variatieKleuren(opbouw.voetM.length, v, s + 2),
      ligger: variatieKleuren(opbouw.liggerM.length, v * 0.8, s + 3),
      conn: variatieKleuren(opbouw.connM.length, v * 0.8, s + 4),
      diag: variatieKleuren(opbouw.diagM.length, v, s + 5),
      hor: variatieKleuren(opbouw.horM.length, v, s + 6),
      ds: variatieKleuren(opbouw.dsM.length, v, s + 7),
      bev: variatieKleuren(opbouw.bevM.length, v * 0.45, s + 8),
    }
  }, [opbouw, opp.variatie, ind.cfg.seed])

  useLayoutEffect(() => {
    vulInstanties(staanders.current, opbouw.staanderM, kleuren.staander)
    vulInstanties(voetplaten.current, opbouw.voetM, kleuren.voet)
    vulInstanties(liggers.current, opbouw.liggerM, kleuren.ligger)
    vulInstanties(connectoren.current, opbouw.connM, kleuren.conn)
    vulInstanties(diagonalen.current, opbouw.diagM, kleuren.diag)
    vulInstanties(horizontalen.current, opbouw.horM, kleuren.hor)
    vulInstanties(doorschuif.current, opbouw.dsM, kleuren.ds)
    vulInstanties(beveiliging.current, opbouw.bevM, kleuren.bev)
    vulInstanties(platen.current, opbouw.plaatM)
    vulInstanties(lichtlijnen.current, opbouw.lijnM)
    const l = labels.current
    if (l) {
      vulInstanties(l, opbouw.labelM)
      const vak = new Float32Array(opbouw.labelVak.length * 2)
      opbouw.labelVak.forEach((n, i) => {
        vak[i * 2] = (n % ATLAS_KOLOMMEN) / ATLAS_KOLOMMEN
        vak[i * 2 + 1] = Math.floor(n / ATLAS_KOLOMMEN) / ATLAS_KOLOMMEN
      })
      l.geometry.setAttribute('atlasVak', new THREE.InstancedBufferAttribute(vak, 2))
    }
  }, [opbouw, kleuren, obj.lichtlijnen])

  // Labels en platen: canvastexturen, eenmalig per seed.
  const etiket = useMemo(() => {
    const atlas = locatieAtlas(ind.cfg.seed + 17)
    return { atlas, materiaal: atlasMateriaal(atlas), plaat: draagvermogen() }
  }, [ind.cfg.seed])
  useEffect(() => () => { etiket.atlas.dispose(); etiket.materiaal.dispose(); etiket.plaat.dispose() }, [etiket])

  // De staandertextuur herhaalt over de hoogte van de staander, niet één keer
  // uitgerekt over 7,6 m: dan werden de sleuven strepen.
  useLayoutEffect(() => {
    if (!set) return
    const h = ind.stellingHoogte / STAANDER_TEGEL
    for (const t of [set.staander.map, set.staander.roughnessMap, set.staander.normalMap]) {
      t.repeat.set(1, Math.round(h))
      t.needsUpdate = true
    }
  }, [set, ind.stellingHoogte])

  if (!obj.stellingen) return null

  // Staal krijgt een gedempte normal: de staanders zijn 9 cm breed en 6 m hoog,
  // dus de textuur staat verticaal flink uitgerekt. Fijne korrel verdraagt dat,
  // een diep reliëf zou als smeer gaan lezen.
  const tex = set
    ? {
        map: set.staal.map,
        roughnessMap: set.staal.roughnessMap,
        normalMap: set.staal.normalMap,
        normalScale: new THREE.Vector2(opp.relief * 0.55, opp.relief * 0.55),
      }
    : {}

  const staanderTex = set
    ? {
        map: set.staander.map,
        roughnessMap: set.staander.roughnessMap,
        normalMap: set.staander.normalMap,
        normalScale: new THREE.Vector2(opp.relief, opp.relief),
      }
    : {}

  const staal = { color: mat.staal, roughness: 0.48, metalness: 0.72, ...tex }
  const lijnKleur = new THREE.Color(licht.accentKleur).multiplyScalar(LICHTLIJN_FEL)
  const liggerMat = { color: mat.ligger, roughness: 0.42, metalness: 0.55, ...tex }

  return (
    <group>
      <instancedMesh ref={staanders} args={[null!, null!, opbouw.staanderM.length]} castShadow receiveShadow>
        <boxGeometry args={[STELLING.staanderDikte, ind.stellingHoogte, STELLING.staanderDikte]} />
        <meshStandardMaterial {...staal} {...staanderTex} />
      </instancedMesh>

      <instancedMesh ref={voetplaten} args={[null!, null!, opbouw.voetM.length]} receiveShadow>
        <boxGeometry args={[0.24, 0.025, 0.22]} />
        <meshStandardMaterial {...staal} metalness={0.6} />
      </instancedMesh>

      <instancedMesh ref={liggers} args={[null!, null!, opbouw.liggerM.length]} castShadow receiveShadow>
        <boxGeometry args={[STELLING.bayBreedte - 0.08, STELLING.liggerHoogte, 0.06]} />
        <meshStandardMaterial {...liggerMat} />
      </instancedMesh>

      <instancedMesh ref={connectoren} args={[null!, null!, opbouw.connM.length]}>
        <boxGeometry args={[0.06, STELLING.liggerHoogte + 0.07, 0.1]} />
        <meshStandardMaterial {...liggerMat} metalness={0.7} roughness={0.35} />
      </instancedMesh>

      {/* Schoren: dunne profielen, geschaald via de matrix op lengte. */}
      <instancedMesh ref={diagonalen} args={[null!, null!, opbouw.diagM.length]}>
        <boxGeometry args={[0.045, 0.9, 0.045]} />
        <meshStandardMaterial {...staal} roughness={0.55} />
      </instancedMesh>

      <instancedMesh ref={horizontalen} args={[null!, null!, opbouw.horM.length]}>
        <boxGeometry args={[0.045, 0.045, STELLING.diepte - 0.16]} />
        <meshStandardMaterial {...staal} roughness={0.55} />
      </instancedMesh>

      <instancedMesh ref={doorschuif} args={[null!, null!, opbouw.dsM.length]}>
        <boxGeometry args={[STELLING.bayBreedte - 0.14, 0.04, 0.04]} />
        <meshStandardMaterial color="#8a8f98" roughness={0.6} metalness={0.5} {...tex} />
      </instancedMesh>

      <instancedMesh ref={labels} args={[undefined, etiket.materiaal, opbouw.labelM.length]}>
        <planeGeometry args={[0.2, 0.1]} />
      </instancedMesh>

      <instancedMesh ref={platen} args={[undefined, undefined, opbouw.plaatM.length]}>
        <planeGeometry args={[0.21, 0.28]} />
        <meshStandardMaterial map={etiket.plaat} roughness={0.5} metalness={0.1} />
      </instancedMesh>

      {obj.lichtlijnen && (
        <instancedMesh ref={lichtlijnen} args={[null!, null!, opbouw.lijnM.length]}>
          <boxGeometry args={[STELLING.bayBreedte - 0.14, 0.026, 0.028]} />
          <meshBasicMaterial color={lijnKleur} />
        </instancedMesh>
      )}

      <instancedMesh ref={beveiliging} args={[null!, null!, opbouw.bevM.length]} castShadow>
        <boxGeometry args={[0.16, 0.44, 0.16]} />
        <meshStandardMaterial color="#F5B301" roughness={0.55} metalness={0.15} {...tex} />
      </instancedMesh>
    </group>
  )
}
